/* DockDash: the How it works tour.
   A second copy of the 3D viewer for the How it works page only: the camera visits each part in turn,
   and the part cards beside it jump the model to that part. The homepage viewer (interact.js) is separate.
   Everything here is progressive enhancement: the page works without this file. */
(function () {
  'use strict';

  var root = document.documentElement;

  function motionOk() { return root.classList.contains('motion-ok'); }
  function clamp(v, a, b) { return Math.min(b, Math.max(a, v)); }
  function ease(t) { t = clamp(t, 0, 1); return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function wrap180(d) { return ((d + 540) % 360) - 180; }
  var FILM_SPEED = 1.35;                    // plays the camera sequence 35% faster; its timings below stay in sequence seconds

  /* ---------- 3D viewer ----------
     Renders the real prototype geometry (v037 non-metal CAD, assets/visordock-model.json: the six printed
     parts exactly as exported) with a small WebGL renderer, no library. The thumb lever turns the comb
     about the axle at (8, 55.5) mm; each card moves out by the same formula as the CAD's reference cards.
     Nothing is exaggerated for effect: lever travel is 0 to 35 degrees and the cards follow it. */
  var MAX_ANGLE = 35;                      // lever travel, degrees (CAD: closed 0 / open 35)
  var PIVOT = [8 - 52, 33 - 55.5];         // lever axle in viewer coordinates (mm)
  var CARD_SIZE = [85.6, 53.98, 0.76];     // nominal card, mm
  /* Shown in gold so the geometry reads against the black studio (a render colour, not a product option); neutral cards so the three lanes read apart. */
  var PART_COLORS = { housing: [186, 158, 100], cover: [178, 151, 95], hook: [198, 169, 107], comb: [204, 176, 114], lever: [222, 196, 138], retainer: [210, 183, 124] };
  var LETTER_COLOR = [12, 12, 12];
  var FLUSH_LETTERING = false;             // the v037 CAD's recessed lettering reads CRUISECLIP, so it is drawn in black
  var CARD_COLORS = [[20, 20, 20], [58, 59, 61], [236, 236, 234]];   // edges of the black, graphite and white sample cards
  var LEVER_AT = [-38, -22, -1];           // centre of the thumb lever, for the close view

  /* Card travel past its resting place, in mm (same formula as the CAD's reference cards; cards rest 2.4 mm inside the case). */
  function cardLift(lane, angle) {
    var rad = 22.5 + 12 * lane;
    return 7.8 * (Math.cos(angle) - 1) + rad * Math.sin(angle);
  }

  /* ----- small matrix helpers (column-major) ----- */
  function ident() { var m = new Float32Array(16); m[0] = m[5] = m[10] = m[15] = 1; return m; }
  function mul(a, b) {
    var o = new Float32Array(16);
    for (var c = 0; c < 4; c++) for (var r = 0; r < 4; r++) {
      var s = 0;
      for (var k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k];
      o[c * 4 + r] = s;
    }
    return o;
  }
  function trans(x, y, z) { var m = ident(); m[12] = x; m[13] = y; m[14] = z; return m; }
  function rotX(a) { var c = Math.cos(a), s = Math.sin(a), m = ident(); m[5] = c; m[6] = s; m[9] = -s; m[10] = c; return m; }
  function rotY(a) { var c = Math.cos(a), s = Math.sin(a), m = ident(); m[0] = c; m[2] = -s; m[8] = s; m[10] = c; return m; }
  function rotZ(a) { var c = Math.cos(a), s = Math.sin(a), m = ident(); m[0] = c; m[1] = s; m[4] = -s; m[5] = c; return m; }
  function persp(fovy, aspect, near, far) {
    var t = 1 / Math.tan(fovy / 2), m = new Float32Array(16);
    m[0] = t / aspect; m[5] = t; m[10] = (far + near) / (near - far); m[11] = -1; m[14] = 2 * far * near / (near - far);
    return m;
  }

  /* ----- geometry ----- */
  function b64(str, Type) {
    var bin = atob(str), n = bin.length, bytes = new Uint8Array(n);
    for (var i = 0; i < n; i++) bytes[i] = bin.charCodeAt(i);
    return new Type(bytes.buffer);
  }

  /* Flat-shaded triangles: 6 floats per vertex (position, face normal). */
  function flatten(pos, idx, scale) {
    var out = new Float32Array(idx.length * 6);
    for (var t = 0; t < idx.length; t += 3) {
      var a = idx[t] * 3, b = idx[t + 1] * 3, c = idx[t + 2] * 3;
      var ax = pos[a] * scale, ay = pos[a + 1] * scale, az = pos[a + 2] * scale;
      var bx = pos[b] * scale, by = pos[b + 1] * scale, bz = pos[b + 2] * scale;
      var cx = pos[c] * scale, cy = pos[c + 1] * scale, cz = pos[c + 2] * scale;
      var ux = bx - ax, uy = by - ay, uz = bz - az, vx = cx - ax, vy = cy - ay, vz = cz - az;
      var nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
      var l = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
      nx /= l; ny /= l; nz /= l;
      var o = t * 6;
      out[o] = ax; out[o + 1] = ay; out[o + 2] = az; out[o + 3] = nx; out[o + 4] = ny; out[o + 5] = nz;
      out[o + 6] = bx; out[o + 7] = by; out[o + 8] = bz; out[o + 9] = nx; out[o + 10] = ny; out[o + 11] = nz;
      out[o + 12] = cx; out[o + 13] = cy; out[o + 14] = cz; out[o + 15] = nx; out[o + 16] = ny; out[o + 17] = nz;
    }
    return out;
  }

  /* A card with rounded corners (ISO/IEC 7810 ID-1: 3.18 mm radius), centred, faces on +z/-z; outline runs anticlockwise seen from +z. */
  function roundedCardData(w, h, d, r) {
    var pts = [], seg = 8, x = w / 2 - r, y = h / 2 - r, z = d / 2;
    [[x, y, 0], [-x, y, 90], [-x, -y, 180], [x, -y, 270]].forEach(function (c) {
      for (var s = 0; s <= seg; s++) { var a = (c[2] + 90 * s / seg) * Math.PI / 180; pts.push([c[0] + r * Math.cos(a), c[1] + r * Math.sin(a)]); }
    });
    var n = pts.length, pos = [0, 0, z, 0, 0, -z], idx = [];
    pts.forEach(function (p) { pos.push(p[0], p[1], z, p[0], p[1], -z); });
    for (var i = 0; i < n; i++) {
      var j = (i + 1) % n, ti = 2 + i * 2, tj = 2 + j * 2;
      idx.push(0, ti, tj, 1, tj + 1, ti + 1, ti + 1, tj + 1, tj, ti + 1, tj, ti);
    }
    return flatten(pos, idx, 1);
  }

  /* A prop note folded in quarters, 56 x 30 x 0.5 mm, centred: three polymer layers with the fold at +x (the end that goes
     in under the clip) and the loose edges staggered at -x. */
  function foldedNoteData() {
    function sheet(w, h, x, z) { var b = boxData(w, h, 0.14); for (var i = 0; i < b.length; i += 6) { b[i] += x; b[i + 2] += z; } return b; }
    var pos = [], idx = [], r = 0.24, seg = 8;
    for (var s = 0; s <= seg; s++) { var a = -Math.PI / 2 + Math.PI * s / seg; pos.push(28 + r * Math.cos(a), -15, r * Math.sin(a), 28 + r * Math.cos(a), 15, r * Math.sin(a)); }
    for (var s2 = 0; s2 < seg; s2++) { var A = s2 * 2, B = A + 1, C = A + 2, D = A + 3; idx.push(A, B, C, C, B, D); }
    var parts = [sheet(55, 30, 0.5, 0.17), sheet(54.4, 29.6, 0.8, 0), sheet(56, 30, 0, -0.17), flatten(pos, idx, 1)];
    var n = parts.reduce(function (s, p) { return s + p.length; }, 0), out = new Float32Array(n), o = 0;
    parts.forEach(function (p) { out.set(p, o); o += p.length; });
    return out;
  }

  /* The prop note's face: red polymer colours (like a 0, so it stands apart from the gold case), a clear window, fine line work and a large 20, marked PROP / NOT LEGAL TENDER.
     Deliberately generic: no portrait, crest or issuer, so it is not a reproduction of a real banknote. */
  function noteTexture(gl) {
    var c = document.createElement('canvas'); c.width = 1024; c.height = 512;
    var x = c.getContext('2d');
    var bg = x.createLinearGradient(0, 0, 1024, 512);
    bg.addColorStop(0, '#f2a27c'); bg.addColorStop(0.55, '#e0674a'); bg.addColorStop(1, '#b8402f');
    x.fillStyle = bg; x.fillRect(0, 0, 1024, 512);
    x.lineWidth = 1.3; x.strokeStyle = 'rgba(110, 24, 14, 0.22)';
    for (var k = 0; k < 34; k++) {
      x.beginPath();
      for (var px = 0; px <= 1024; px += 8) { var py = 40 + k * 13 + Math.sin(px / 46 + k * 0.5) * 9 + Math.sin(px / 130) * 14; px ? x.lineTo(px, py) : x.moveTo(px, py); }
      x.stroke();
    }
    x.strokeStyle = 'rgba(130, 34, 20, 0.35)'; x.lineWidth = 1;
    for (var q = 0; q < 48; q++) { x.beginPath(); x.ellipse(690, 250, 150, 150 - q * 3, q * Math.PI / 48, 0, Math.PI * 2); x.stroke(); }
    // clear window
    var w = x.createLinearGradient(60, 0, 230, 0);
    w.addColorStop(0, 'rgba(226, 238, 242, 0.95)'); w.addColorStop(0.5, 'rgba(244, 250, 252, 0.98)'); w.addColorStop(1, 'rgba(214, 230, 236, 0.95)');
    x.fillStyle = w; x.fillRect(60, 0, 170, 512);
    x.strokeStyle = 'rgba(140, 160, 170, 0.6)'; x.lineWidth = 2; x.strokeRect(60, -2, 170, 516);
    x.strokeStyle = 'rgba(150, 175, 185, 0.7)'; x.beginPath(); x.arc(145, 256, 48, 0, Math.PI * 2); x.stroke();
    x.fillStyle = 'rgba(96, 18, 10, 0.88)'; x.textBaseline = 'alphabetic';
    x.font = '800 230px Archivo, "Helvetica Neue", Arial, sans-serif'; x.textAlign = 'right'; x.fillText('20', 990, 330);
    x.font = '700 30px Archivo, "Helvetica Neue", Arial, sans-serif'; x.textAlign = 'left'; x.fillText('PROP', 270, 440);
    x.font = '600 17px Archivo, "Helvetica Neue", Arial, sans-serif'; x.fillStyle = 'rgba(96, 18, 10, 0.7)';
    var line = ''; for (var m = 0; m < 12; m++) line += 'NOT LEGAL TENDER \u00b7 PROP \u00b7 ';
    x.fillText(line, 250, 30); x.fillText(line, 250, 496);
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return tex;
  }

  /* The sun visor the holder is shown on: a typical 330 x 165 x 20 mm cloth visor. Its end edge sits in the U-dock's gap
     (viewer x -26, z -27.2 to -7.2, against the 20 mm hook opening), the lever end of the case stays clear past that edge,
     and a closed vanity mirror flap sits on the driver's side further along. Illustrative, not a particular car. */
  var VISOR = [330, 165, 20];
  /* Folding up: the visor turns about its hinge along the top edge (viewer y 123, z -17.2), swinging its free edge toward
     the windscreen. The face the holder is on ends up facing down into the car, so the holder and anything clipped to it
     hang below the folded visor, never between it and the roof. The roof lining sits 17 mm above the hinge line. */
  var HINGE = [123, -17.2], FOLD_ANGLE = 84, ROOF_Y = 140, ROOF_COLOR = [38, 39, 42];
  var CASE_SLIDE = 70;                     // the case slides this far along its rail onto the U-dock as it clips on
  function foldMatrix(k) {
    return mul(trans(0, HINGE[0], HINGE[1]), mul(rotX(k * FOLD_ANGLE * Math.PI / 180), trans(0, -HINGE[0], -HINGE[1])));
  }
  /* The roof lining: one face, facing down, so it shows from inside the car and vanishes from a camera above it. */
  function roofData() {
    var y = ROOF_Y, x0 = -1400, x1 = 1700, z0 = -1600, z1 = 900;   // big enough that its edges stay out of shot
    return flatten([x0, y, z0, x1, y, z0, x1, y, z1, x0, y, z1], [0, 1, 2, 0, 2, 3], 1);
  }
  var VISOR_COLOR = [124, 126, 130], FLAP_COLOR = [70, 72, 76];
  function moved(d, x, y, z) { for (var i = 0; i < d.length; i += 6) { d[i] += x; d[i + 1] += y; d[i + 2] += z; } return d; }
  /* Grey woven headliner cloth, drawn here and tiled every 30 mm. */
  function fabricTexture(gl) {
    var c = document.createElement('canvas'); c.width = c.height = 256;
    var x = c.getContext('2d'), img = x.createImageData(256, 256), seed = 7;
    function rnd() { seed = (seed * 16807) % 2147483647; return seed / 2147483647; }
    for (var py = 0; py < 256; py++) for (var px = 0; px < 256; px++) {
      var weave = (((px >> 1) + (py >> 1)) & 1) ? 7 : -7;
      var v = 122 + weave + (rnd() - 0.5) * 18 + Math.sin(py * 0.8) * 3, o = (py * 256 + px) * 4;
      img.data[o] = v; img.data[o + 1] = v + 2; img.data[o + 2] = v + 5; img.data[o + 3] = 255;
    }
    x.putImageData(img, 0, 0);
    var tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, c);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
    return tex;
  }

  function boxData(w, h, d) {
    var x = w / 2, y = h / 2, z = d / 2;
    var p = [[-x, -y, -z], [x, -y, -z], [x, y, -z], [-x, y, -z], [-x, -y, z], [x, -y, z], [x, y, z], [-x, y, z]];
    var quads = [[4, 5, 6, 7], [1, 0, 3, 2], [5, 1, 2, 6], [0, 4, 7, 3], [7, 6, 2, 3], [0, 1, 5, 4]];
    var pos = [], idx = [];
    p.forEach(function (v) { pos.push(v[0], v[1], v[2]); });
    quads.forEach(function (q) { idx.push(q[0], q[1], q[2], q[0], q[2], q[3]); });
    return flatten(pos, idx, 1);
  }


  /* The card faces: assets/card-faces.webp holds the black, graphite and white sample cards stacked in thirds. */
  function loadCardAtlas(gl, url, done) {
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      var tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      var an = gl.getExtension('EXT_texture_filter_anisotropic');
      if (an) gl.texParameterf(gl.TEXTURE_2D, an.TEXTURE_MAX_ANISOTROPY_EXT, Math.min(8, gl.getParameter(an.MAX_TEXTURE_MAX_ANISOTROPY_EXT)));
      done(tex);
    };
    img.src = url;
  }

  function hexToRgb(hex) {
    return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
  }

  /* ----- shaders ----- */
  /* A black studio: low fill, one soft white key from above left, a cool-neutral back light that
     catches the edges, and a satin highlight like matte-printed PLA. */
  var VERT = 'attribute vec3 aPos;attribute vec3 aNor;uniform mat4 uProj;uniform mat4 uMv;varying vec3 vN;varying vec3 vP;varying float vY;varying vec3 vL;varying vec3 vNL;' +
    'void main(){vec4 p=uMv*vec4(aPos,1.0);vP=p.xyz;vY=aPos.y;vL=aPos;vNL=aNor;vN=(uMv*vec4(aNor,0.0)).xyz;gl_Position=uProj*p;}';
  var FRAG = 'precision mediump float;varying vec3 vN;varying vec3 vP;varying float vY;varying vec3 vL;varying vec3 vNL;uniform sampler2D uTex;uniform float uTexOn;uniform float uAlpha;uniform vec3 uColor;uniform float uMetal;uniform float uMirror;' +
    'void main(){' +
    'vec3 n=normalize(vN);vec3 v=normalize(-vP);' +
    'vec3 base=pow(uColor,vec3(2.2));' +
    /* printed card face: the +z face of a card box (85.6 x 53.98 mm, centred) takes row uTexOn (1 to 3) of the card atlas */
    'if(uTexOn>3.5&&vNL.z>0.5)base=pow(texture2D(uTex,vec2(vL.x/56.0+0.5,0.5-vL.y/30.0)).rgb,vec3(2.2));' +
    'else if(uTexOn>0.5&&vNL.z>0.5)base=pow(texture2D(uTex,vec2(vL.x/85.6+0.5,(uTexOn-0.5-vL.y/53.98)/3.0)).rgb,vec3(2.2));' +
    'if(uTexOn>4.5)base=pow(texture2D(uTex,(abs(vNL.y)>0.5?vL.xz:vL.xy)/30.0).rgb,vec3(2.2))*(abs(vNL.x)>0.5?0.88:1.0);' +
    'vec3 L1=normalize(vec3(-0.5,0.8,0.45));vec3 L2=normalize(vec3(0.85,0.25,0.3));vec3 L3=normalize(vec3(0.3,0.6,-0.75));vec3 L4=normalize(vec3(0.1,-1.0,0.35));' +
    'float key=max(dot(n,L1),0.0);float fill=max(dot(n,L2),0.0);' +
    'float sky=mix(0.03,0.12,n.y*0.5+0.5);' +
    'float s1=pow(max(dot(n,normalize(L1+v)),0.0),70.0)*0.32+pow(max(dot(n,normalize(L1+v)),0.0),8.0)*0.11;' +
    'float s2=pow(max(dot(n,normalize(L2+v)),0.0),24.0)*0.12;' +
    'float fr=pow(1.0-max(dot(n,v),0.0),4.0);' +
    'float back=max(dot(n,L3),0.0)*0.5+fr*0.35;' +
    'vec3 tint=mix(vec3(1.0),base/max(max(base.r,base.g),max(base.b,0.001)),0.85);' +
    /* white underlight: a cool-white bounce from the floor on the faces that look down */
    'float under=pow(max(dot(n,vec3(0.0,-1.0,0.15)),0.0),1.5);' +
    'float g=0.62+0.62*smoothstep(-70.0,70.0,-vP.x*0.9+vP.y*0.7);' +
    /* soft occlusion: the lower part of the case sits in the floor's shade, so it darkens toward the base */
    'float ao=mix(0.6,1.0,smoothstep(-46.0,8.0,vY));' +
    'vec3 c=(base*(sky+1.05*key+0.28*fill)*g*ao)+tint*vec3(s1*1.3+s2*1.2)*(0.6+0.5*g)+tint*back*0.22+vec3(0.95,0.97,1.0)*under*0.55;' +
    /* studio reflections: a big soft box above left, a strip light on the right, a white floor below */
    'vec3 r=reflect(-v,n);' +
    'float e1=smoothstep(0.7,0.96,dot(r,normalize(vec3(-0.4,0.9,0.5))));' +
    'float e2=smoothstep(0.78,0.97,dot(r,normalize(vec3(0.95,0.25,0.15))))*0.8;' +
    'float e3=smoothstep(0.1,0.9,-r.y)*0.55;' +
    'float e4=smoothstep(0.82,0.99,dot(r,normalize(vec3(-0.9,0.1,-0.4))))*0.5;' +
    'vec3 env=vec3(1.0)*(e1*1.1+e2+e4)+vec3(0.95,0.97,1.0)*e3+vec3(0.04);' +
    'float refl=uMetal*(0.30+0.5*fr)+(1.0-uMetal)*(0.04+0.16*fr);' +
    'c+=env*mix(vec3(1.0),tint,uMetal*0.85)*refl*0.42;' +
    /* printed card faces read as matte printed plastic: mostly diffuse, a little sheen, so black cards stay black */
    'if(uTexOn>0.5&&vNL.z>0.5)c=mix(c,base*(0.3+0.95*key+0.3*fill)*g+vec3(s1*0.5),0.88);' +
    /* the visor's cloth: no gloss, just a soft sheen at grazing angles */
    'if(uTexOn>4.5)c=base*(0.4+0.85*key+0.3*fill)*g+vec3(0.9,0.92,0.95)*fr*0.06;' +
    /* glossy plastic (uMetal 2, the sunglasses frame): a soft lit body under a clear coat that mirrors the studio's soft boxes,
       with tight white highlights and reflections that grow toward grazing angles (Fresnel) */
    'if(uMetal>1.5){vec3 m=dot(n,v)<0.0?-n:n;vec3 rr=reflect(-v,m);float f=0.04+0.96*pow(1.0-max(dot(m,v),0.0),5.0);' +
    'float b1=smoothstep(0.82,0.97,dot(rr,normalize(vec3(-0.4,0.9,0.5))));float b2=smoothstep(0.88,0.98,dot(rr,normalize(vec3(0.95,0.25,0.15))));' +
    'float hs=pow(max(dot(m,normalize(L1+v)),0.0),160.0)*1.6+pow(max(dot(m,normalize(L2+v)),0.0),90.0)*0.7;' +
    'float d=0.16+0.75*max(dot(m,L1),0.0)+0.25*max(dot(m,L2),0.0)+0.12*max(dot(m,vec3(0.0,-1.0,0.2)),0.0);' +
    'c=base*d*0.92+vec3(b1*1.1+b2*0.9)*(0.10+0.9*f)+vec3(hs);}' +
    /* filmic curve (ACES fit): softer highlights and deeper blacks, like a camera rather than a flat render */
    'c*=0.86;c=(c*(2.51*c+0.03))/(c*(2.43*c+0.59)+0.14);' +
    'vec3 o=pow(c,vec3(1.0/2.2));' +
    /* a touch of film grain so smooth surfaces don't read as a computer gradient */
    'o+=(fract(sin(dot(gl_FragCoord.xy,vec2(12.9898,78.233)))*43758.5453)-0.5)*0.014;' +
    'float fade=uMirror*0.06*pow(clamp(1.0-(vY+46.0)/26.0,0.0,1.0),1.8);' +
    'if(uMetal<0.0){float k=max(dot(n,L1),0.0);o=vec3(0.02+0.05*k);}' +
    'gl_FragColor=mix(vec4(o,1.0),vec4(o*fade,fade),uMirror)*uAlpha;}';
  /* The surface it sits on: dark pebbled soft-touch, like a car dashboard, lit by the key from above left,
     with a soft shadow and a tighter contact shadow where the case meets it. It fades out at the edges into
     the backdrop behind the stage (set in how-tour.liquid), so there's no visible floor edge. */
  var FLOOR_VERT = 'attribute vec3 aPos;uniform mat4 uProj;uniform mat4 uMv;varying vec2 vQ;' +
    'void main(){vQ=aPos.xz;gl_Position=uProj*uMv*vec4(aPos,1.0);}';
  var FLOOR_FRAG = '#ifdef GL_FRAGMENT_PRECISION_HIGH\nprecision highp float;\n#else\nprecision mediump float;\n#endif\nvarying vec2 vQ;' +
    'float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}' +
    'float vn(vec2 p){vec2 i=floor(p);vec2 f=fract(p);f=f*f*(3.0-2.0*f);' +
    'return mix(mix(h(i),h(i+vec2(1.0,0.0)),f.x),mix(h(i+vec2(0.0,1.0)),h(i+vec2(1.0,1.0)),f.x),f.y);}' +
    'void main(){' +
    'float edge=1.0-smoothstep(0.2,0.85,length(vQ/vec2(210.0,120.0)));' +
    'float key=1.0-smoothstep(0.0,1.0,length((vQ-vec2(-50.0,10.0))/vec2(210.0,140.0)));' +
    'float grain=vn(vQ*0.8)*0.5+vn(vQ*2.1)*0.3+vn(vQ*0.12)*0.2;' +
    'vec3 base=vec3(0.052,0.047,0.042)*(0.75+0.5*grain);' +
    'float sh=1.0-smoothstep(0.1,1.0,length((vQ-vec2(0.0,-6.0))/vec2(74.0,34.0)));' +
    'float contact=1.0-smoothstep(0.0,1.0,length((vQ-vec2(0.0,-2.0))/vec2(60.0,12.0)));' +
    'vec3 col=base*(0.25+1.1*key)*(1.0-0.6*sh)*(1.0-0.5*contact);' +
    'col+=vec3(1.0,0.96,0.9)*0.035*pow(key,3.0)*(0.7+0.6*grain);' +
    'col=pow(col,vec3(1.0/2.2));' +
    'gl_FragColor=vec4(col*edge,edge);}';

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
  }

  function makeProgram(gl, vsrc, fsrc) {
    var vs = compile(gl, gl.VERTEX_SHADER, vsrc), fs = compile(gl, gl.FRAGMENT_SHADER, fsrc);
    if (!vs || !fs) return null;
    var p = gl.createProgram();
    gl.attachShader(p, vs); gl.attachShader(p, fs);
    gl.bindAttribLocation(p, 0, 'aPos'); gl.bindAttribLocation(p, 1, 'aNor');
    gl.linkProgram(p);
    return gl.getProgramParameter(p, gl.LINK_STATUS) ? p : null;
  }

  /* ==========================================================================
     How it works tour (sections/how-tour.liquid). Its own viewer, so the homepage
     model and its camera sequence stay exactly as they are.
     Same CAD, same renderer and studio light; the camera visits one part at a time,
     and each part card on the page jumps the model to that part.
     ========================================================================== */

  /* Each part: the camera pose, and what moves while the camera holds there.
     open = lever travel (0 closed, 1 = 35 degrees), glasses = sunglasses clipped in (0 out, 1 in),
     notes = folded notes under the money clip, hook = hook on its rail (0 slid off, 1 seated).
     [start, end] pairs animate during the hold. */
  var PARTS = {
    // overview: the case slides onto the U-dock that is already on the visor
    overview: { rx: -16, ry: -52, zoom: 1.12, at: [22, 4, 0], open: [0, 0], glasses: [0, 0], notes: [0, 0], hook: [0, 1], fold: [0, 0] },
    lever: { rx: -30, ry: -14, zoom: 0.56, at: LEVER_AT, open: [0, 1], glasses: [0, 0], notes: [0, 0], hook: [1, 1], fold: [0, 0] },
    cards: { rx: -20, ry: -46, zoom: 0.86, at: [18, 0, 0], open: [1, 1], glasses: [0, 0], notes: [0, 0], hook: [1, 1], fold: [0, 0] },
    glasses: { rx: -14, ry: -26, zoom: 1.34, at: [46, 0, 10], open: [0, 0], glasses: [0, 1], notes: [0, 0], hook: [1, 1], fold: [0, 0] },
    cash: { rx: -36, ry: 16, zoom: 0.64, at: [-30, 0, 6], open: [0, 0], glasses: [0, 0], notes: [0, 1], hook: [1, 1], fold: [0, 0] },
    // hook: from the lever end, looking along the visor's edge into the U-dock
    hook: { rx: -14, ry: 96, zoom: 0.72, at: [-34, 0, -14], open: [0, 0], glasses: [0, 0], notes: [0, 0], hook: [0, 1], fold: [0, 0] },
    // fold: side on from the lever end, so the visor swings up to the roof with the holder hanging below it
    fold: { rx: -8, ry: 96, zoom: 2, at: [0, 100, -70], open: [0, 0], glasses: [1, 1], notes: [0, 0], hook: [1, 1], fold: [0, 1] },
    case: { rx: -24, ry: -34, zoom: 1.36, at: [20, 0, 6], open: [1, 1], glasses: [1, 1], notes: [1, 1], hook: [1, 1], fold: [0, 0] }
  };
  var MOVE = 1.7, HOLD = 2.8;              // seconds the camera travels, then holds on each part
  var GLASSES_DROP = 150;                  // the sunglasses slide in along the cradle from this far above
  var HOOK_SLIDE = CASE_SLIDE;             // and the case slides onto the hook's rail from this far out
  var NOTES_SLIDE = 90;                    // folded notes slide under the money clip from the lever end
  var NOTE_COLOR = [214, 120, 96];         // the polymer edge of the prop note; its face is drawn in noteTexture()

  /* ----- the sunglasses: assets/glasses-model.json, already folded and placed in viewer coordinates -----
     A third-party eyeglasses frame (see the file's source note) with both temples folded at the hinges, no lenses.
     One temple runs through the v037 cradle at the card end, the other lies over the cradle plate, and the frame hangs past the case. Built by tools/build-glasses.js; rebuild it if the cradle moves. */

  /* Hold a pose, then ease to the next: one entry per part, in the order the cards are on the page. */
  function buildTour(keys) {
    var list = ['overview'].concat(keys.filter(function (k) { return PARTS[k] && k !== 'overview'; }));
    var steps = [], t = 0, ry = PARTS.overview.ry;
    list.forEach(function (key, i) {
      var p = PARTS[key];
      ry = ry + wrap180(p.ry - ry);
      steps.push({ key: key, t0: t, t1: t + (i ? MOVE : 0), t2: t + (i ? MOVE : 0) + (i ? HOLD : 3.2), rx: p.rx, ry: ry, zoom: p.zoom, at: p.at, open: p.open, glasses: p.glasses, notes: p.notes, hook: p.hook, fold: p.fold });
      t = steps[steps.length - 1].t2;
    });
    return { steps: steps, end: t };
  }
  function tourAt(tour, t) {
    var s = tour.steps, i = 0;
    while (i < s.length - 1 && t >= s[i + 1].t0) i++;
    var b = s[i], a = s[Math.max(0, i - 1)];
    function mix(x, y, k) { return x + (y - x) * k; }
    var out = { key: b.key, at: [0, 0, 0] };
    if (t < b.t1) {
      var k = ease((t - b.t0) / (b.t1 - b.t0));
      out.rx = mix(a.rx, b.rx, k); out.ry = mix(a.ry, b.ry, k); out.zoom = mix(a.zoom, b.zoom, k);
      for (var j = 0; j < 3; j++) out.at[j] = mix(a.at[j], b.at[j], k);
      var quick = ease((t - b.t0) / 0.45);
      ['open', 'glasses', 'notes', 'hook', 'fold'].forEach(function (m) {
        var leaving = (m === 'glasses' || m === 'notes') && b[m][0] < a[m][1];
        out[m] = mix(a[m][1], b[m][0], leaving ? quick : k);
      });
    } else {
      var h = ease((t - b.t1 - 0.2) / Math.max(0.1, b.t2 - b.t1 - 0.6));
      out.rx = b.rx; out.ry = b.ry; out.zoom = b.zoom; out.at = b.at.slice();
      ['open', 'glasses', 'notes', 'hook', 'fold'].forEach(function (m) { out[m] = mix(b[m][0], b[m][1], h); });
    }
    return out;
  }

  function initTour(el) {
    if (el.hasAttribute('data-ready')) return;
    var canvas = el.querySelector('[data-v3d-canvas]');
    var stage = el.querySelector('[data-v3d-stage]');
    var modelUrl = el.getAttribute('data-model');
    var glassesUrl = el.getAttribute('data-glasses');
    if (!canvas || !stage || !modelUrl || !glassesUrl) return;

    var gl = null;
    try { gl = canvas.getContext('webgl', { antialias: true, alpha: true, premultipliedAlpha: true }) || canvas.getContext('experimental-webgl'); } catch (e) { gl = null; }
    var program = gl && makeProgram(gl, VERT, FRAG);
    var floorProgram = gl && makeProgram(gl, FLOOR_VERT, FLOOR_FRAG);
    if (!program || !floorProgram) return;   // no WebGL: the still image stays, and the cards still read as a list
    el.setAttribute('data-ready', '');

    var slider = el.querySelector('[data-v3d-open]');
    var readout = el.querySelector('[data-v3d-readout]');
    var playBtn = el.querySelector('[data-v3d-play]');
    var presetBtns = Array.prototype.slice.call(el.querySelectorAll('[data-preset]'));
    var partBtns = Array.prototype.slice.call(el.querySelectorAll('[data-part]'));
    var caption = el.querySelector('[data-tour-caption]');
    var frameColor = hexToRgb(el.getAttribute('data-frame') || '#F2F2F0');

    var tour = buildTour(partBtns.map(function (b) { return b.getAttribute('data-part'); }));
    var reduce = !motionOk();
    var START = PARTS.case;
    var view = { rx: START.rx, ry: START.ry, zoom: START.zoom, at: START.at.slice(), open: 1, glasses: 1, notes: 1, hook: 1, fold: 0 };
    var goal = { rx: view.rx, ry: view.ry, zoom: view.zoom, at: view.at.slice(), open: 1, glasses: 1, notes: 1, hook: 1, fold: 0 };
    var speed = { rx: 0, ry: 0, zoom: 0, open: 0, glasses: 0, notes: 0, hook: 0, fold: 0 };
    var KEYS = ['rx', 'ry', 'zoom', 'open', 'glasses', 'notes', 'hook', 'fold'];
    var parts = [], cardAtlas = null, roofBuffer = null, roofVerts = 0, fabricTex = null, visorBuffer = null, visorVerts = 0, flapBuffer = null, flapVerts = 0, noteTex = null, cardBuffer = null, cardVerts = 0, floorBuffer = null, glasses = null, notesBuffer = null, notesVerts = 0;
    var aspect = 1, distance = 300, dpr = 1;
    var film = -1, current = '', pending = 0;
    var played = false, dragging = false, sliding = false, inView = false, seen = false, loaded = false, loading = false;
    var raf = 0, last = 0, vel = 0, lastX = 0, lastY = 0, lastT = 0;
    var still = /[?&]tour-t=([\d.]+)/.exec(location.search);   // ?tour-t=9 freezes the tour at 9 s, for capturing stills

    var uProj = gl.getUniformLocation(program, 'uProj');
    var uMv = gl.getUniformLocation(program, 'uMv');
    var uColor = gl.getUniformLocation(program, 'uColor');
    var uMetal = gl.getUniformLocation(program, 'uMetal');
    var uTexOn = gl.getUniformLocation(program, 'uTexOn');
    var uAlpha = gl.getUniformLocation(program, 'uAlpha');
    var uMirror = gl.getUniformLocation(program, 'uMirror');
    var fProj = gl.getUniformLocation(floorProgram, 'uProj');
    var fMv = gl.getUniformLocation(floorProgram, 'uMv');
    gl.enable(gl.CULL_FACE);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.clearColor(0, 0, 0, 0);

    function upload(data) {
      var buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
      return buf;
    }
    function drawBuffer(buf, count, mv, color, metal, tex, alpha) {
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.enableVertexAttribArray(0); gl.enableVertexAttribArray(1);
      gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 24, 0);
      gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 24, 12);
      gl.uniformMatrix4fv(uMv, false, mv);
      gl.uniform3f(uColor, color[0] / 255, color[1] / 255, color[2] / 255);
      gl.uniform1f(uMetal, metal === undefined ? 1 : metal);
      gl.uniform1f(uAlpha, alpha === undefined ? 1 : alpha);
      var tx = tex === 5 ? fabricTex : tex === 4 ? noteTex : cardAtlas;
      gl.uniform1f(uTexOn, tex && tx ? tex : 0);
      if (tex && tx) gl.bindTexture(gl.TEXTURE_2D, tx);
      gl.drawArrays(gl.TRIANGLES, 0, count);
    }

    function render() {
      if (!loaded) return;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      var angle = clamp(view.open, 0, 1) * MAX_ANGLE * Math.PI / 180;
      var g = clamp(view.glasses, 0, 1), hk = clamp(view.hook, 0, 1), nt = clamp(view.notes, 0, 1);
      var d = distance * view.zoom;
      var orbit = mul(trans(0, d * (aspect < 1.2 ? 0.06 : 0.03), -d), mul(rotX(view.rx * Math.PI / 180), rotY(view.ry * Math.PI / 180)));
      var cam = mul(orbit, trans(-view.at[0], -view.at[1], -view.at[2]));
      var proj = persp(30 * Math.PI / 180, aspect, d * 0.2, d * 4);
      // The floor drops away while the sunglasses are in, since they hang below the case.
      var floorY = -46 - 30 * ease(g);

      gl.disable(gl.DEPTH_TEST); gl.disable(gl.CULL_FACE); gl.enable(gl.BLEND);
      gl.useProgram(floorProgram);
      gl.uniformMatrix4fv(fProj, false, proj);
      gl.uniformMatrix4fv(fMv, false, mul(cam, trans(0, floorY, 0)));
      gl.bindBuffer(gl.ARRAY_BUFFER, floorBuffer);
      gl.enableVertexAttribArray(0); gl.disableVertexAttribArray(1);
      gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 12, 0);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      gl.disable(gl.BLEND); gl.enable(gl.DEPTH_TEST); gl.enable(gl.CULL_FACE);
      gl.useProgram(program);
      gl.uniformMatrix4fv(uProj, false, proj);
      var turn = mul(trans(PIVOT[0], PIVOT[1], 0), mul(rotZ(-angle), trans(-PIVOT[0], -PIVOT[1], 0)));
      // the visor (with the U-dock on it) turns about its hinge; the case slides along its rail onto the dock
      var fold = foldMatrix(ease(clamp(view.fold, 0, 1))), caseOn = trans(HOOK_SLIDE * (1 - ease(hk)), 0, 0);
      function drawModel(world, withGlasses) {
        var onVisor = mul(world, fold), root = mul(onVisor, caseOn);
        parts.forEach(function (p) {
          var m = root;
          if (p.key === 'lever' || p.key === 'comb') m = mul(root, turn);
          else if (p.key === 'hook') m = onVisor;
          drawBuffer(p.buf, p.count, m, p.color, p.metal);
        });
        for (var i = 0; i < 3; i++) {
          var x = (16 + 101.6) / 2 - 52 + cardLift(i, angle);
          var z = 4.4 - (3 + 2 * i);
          drawBuffer(cardBuffer, cardVerts, mul(root, trans(x, 0, z)), CARD_COLORS[i], 0.12, i + 1);
        }
        drawBuffer(visorBuffer, visorVerts, onVisor, VISOR_COLOR, 0.04, 5);
        drawBuffer(flapBuffer, flapVerts, onVisor, FLAP_COLOR, 0.35);
        if (nt > 0.001) {
          // Under the clip: the strip is 2 mm off the face (z 4.4 to 6.4) but its tip lip pinches down to z 5.0, so the folded notes lie flat on the face (z 4.45 to 4.95); they reach in to x -4 and stick out past the tip at x -49.
          drawBuffer(notesBuffer, notesVerts, mul(root, trans(-32 - NOTES_SLIDE * (1 - ease(nt)), 0, 4.7)), NOTE_COLOR, 0.05, 4);
        }
        if (withGlasses && g > 0.001) {
          var gm = mul(root, trans(0, GLASSES_DROP * (1 - ease(g)), 0));
          gl.disable(gl.CULL_FACE);
          drawBuffer(glasses.frame, glasses.frameCount, gm, frameColor, 2);   // 2 = glossy plastic: see FRAG
          gl.enable(gl.CULL_FACE);
        }
      }
      var flip = ident(); flip[5] = -1;
      var mirror = mul(trans(0, floorY, 0), mul(flip, trans(0, -floorY, 0)));
      gl.enable(gl.BLEND); gl.frontFace(gl.CW); gl.uniform1f(uMirror, 1);
      drawModel(mul(cam, mirror), false);
      gl.uniform1f(uMirror, 0); gl.frontFace(gl.CCW); gl.disable(gl.BLEND); gl.clear(gl.DEPTH_BUFFER_BIT);
      drawModel(cam, true);
      // the roof lining (the same cloth as the visor) fades in only while the visor folds up, so it never clutters the other shots
      var roofIn = Math.min(1, clamp(view.fold, 0, 1) * 4);
      if (roofIn > 0.002) { gl.enable(gl.BLEND); drawBuffer(roofBuffer, roofVerts, cam, ROOF_COLOR, 0.02, 5, roofIn); gl.disable(gl.BLEND); }
      var deg = Math.round(clamp(view.open, 0, 1) * MAX_ANGLE);
      if (!sliding) {
        if (slider) { slider.value = deg; slider.style.setProperty('--p', (deg / MAX_ANGLE * 100) + '%'); }
        if (readout) readout.textContent = deg + '°';
      }
    }

    function request() { if (!raf) raf = window.requestAnimationFrame(tick); }

    function tick(t) {
      raf = 0;
      var dt = Math.min(0.1, ((t - (last || t)) / 1000));
      last = t;
      var busy = false;

      if (film >= 0) {
        if (inView && !still) film += dt * FILM_SPEED;   // the sequence runs a little faster than real time
        var f = tourAt(tour, Math.min(film, tour.end));
        view.rx = f.rx; view.ry = f.ry; view.zoom = f.zoom; view.at = f.at; view.open = f.open; view.glasses = f.glasses; view.notes = f.notes; view.hook = f.hook; view.fold = f.fold;
        mark(f.key);
        if (film >= tour.end && !still) endFilm(); else busy = inView && !still;
      } else if (!dragging) {
        if (reduce) {
          KEYS.forEach(function (key) { view[key] = goal[key]; });
          view.at = goal.at.slice();
        } else {
          for (var left = dt; left > 0; left -= 1 / 120) {
            var h = Math.min(left, 1 / 120);
            KEYS.forEach(function (key) {
              var leaving = (key === 'glasses' || key === 'notes') && goal[key] < view[key];
              var stiff = key === 'rx' || key === 'ry' || key === 'zoom' ? 40 : leaving ? 160 : 14, damp = 2 * Math.sqrt(stiff);
              speed[key] += ((goal[key] - view[key]) * stiff - speed[key] * damp) * h;
              view[key] += speed[key] * h;
            });
          }
          KEYS.forEach(function (key) {
            if (Math.abs(goal[key] - view[key]) > (key === 'rx' || key === 'ry' ? 0.02 : 0.001) || Math.abs(speed[key]) > 0.01) busy = true;
            else { view[key] = goal[key]; speed[key] = 0; }
          });
          var k = 1 - Math.exp(-dt * 4);
          for (var a = 0; a < 3; a++) { view.at[a] += (goal.at[a] - view.at[a]) * k; if (Math.abs(goal.at[a] - view.at[a]) > 0.05) busy = true; else view.at[a] = goal.at[a]; }
        }
      }

      render();
      if (busy || dragging) request(); else last = 0;
    }

    /* The card for the part on screen is marked, and its line shows under the model. */
    function mark(key) {
      if (key === current) return;
      current = key;
      var text = '';
      partBtns.forEach(function (btn) {
        var on = btn.getAttribute('data-part') === key;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('is-active', on);
        if (on) { var tt = btn.querySelector('.tour__part-title'); text = tt ? tt.textContent : ''; }
      });
      if (caption) caption.textContent = text;
    }

    function setPlayLabel() {
      if (!playBtn) return;
      playBtn.textContent = film >= 0 ? playBtn.dataset.pause : (played ? playBtn.dataset.replay : playBtn.dataset.play);
      playBtn.setAttribute('aria-pressed', film >= 0 ? 'true' : 'false');
    }
    function startFilm() {
      film = 0; played = true; last = 0;
      el.classList.add('is-playing');
      setPlayLabel();
      request();
    }
    function endFilm() {
      if (film < 0) return;
      film = -1;
      KEYS.forEach(function (key) { goal[key] = view[key]; speed[key] = 0; });
      goal.at = view.at.slice();
      el.classList.remove('is-playing');
      setPlayLabel();
      request();
    }
    function takeover() { endFilm(); played = true; clearTimeout(pending); }

    /* Jump to one part: the camera flies there and that part's movement plays once. */
    function show(key) {
      var p = PARTS[key];
      if (!p) return;
      takeover();
      mark(key);
      goal.rx = p.rx; goal.ry = view.ry + wrap180(p.ry - view.ry); goal.zoom = p.zoom; goal.at = p.at.slice();
      // Set the part's starting state now, then play its movement once the camera has mostly arrived.
      var moves = [];
      ['open', 'glasses', 'notes', 'hook', 'fold'].forEach(function (m) {
        if (p[m][0] !== p[m][1]) { view[m] = goal[m] = p[m][0]; speed[m] = 0; moves.push(m); }
        else goal[m] = p[m][1];
      });
      clearTimeout(pending);
      if (moves.length) pending = setTimeout(function () { moves.forEach(function (m) { goal[m] = p[m][1]; }); request(); }, reduce ? 0 : 900);
      request();
    }
    function go(name) {
      takeover();
      goal.open = name === 'open' ? 1 : 0;
      request();
    }

    stage.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      dragging = true;
      takeover();
      speed.rx = speed.ry = 0;
      stage.classList.add('is-drag');
      try { stage.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      lastX = e.clientX; lastY = e.clientY; lastT = e.timeStamp; vel = 0;
      request();
    });
    stage.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      view.ry += dx * 0.5;
      if (e.pointerType === 'mouse') view.rx = clamp(view.rx + dy * 0.35, -80, 25);
      goal.rx = view.rx; goal.ry = view.ry;
      var dtm = Math.max(1, e.timeStamp - lastT);
      vel = vel * 0.8 + (dx * 0.5 / dtm) * 0.2;
      lastT = e.timeStamp;
      request();
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      stage.classList.remove('is-drag');
      goal.ry = view.ry + clamp(vel * 60, -30, 30);
      request();
    }
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);

    stage.addEventListener('keydown', function (e) {
      var handled = true;
      if (e.key === 'ArrowLeft') { takeover(); goal.ry -= 8; }
      else if (e.key === 'ArrowRight') { takeover(); goal.ry += 8; }
      else if (e.key === 'ArrowUp') { takeover(); goal.rx = clamp(goal.rx - 5, -80, 25); }
      else if (e.key === 'ArrowDown') { takeover(); goal.rx = clamp(goal.rx + 5, -80, 25); }
      else if (e.key === 'Home') { show('case'); }
      else { handled = false; }
      if (handled) { e.preventDefault(); request(); }
    });

    if (slider) {
      slider.setAttribute('max', MAX_ANGLE);
      slider.addEventListener('input', function () {
        takeover();
        sliding = true;
        view.open = goal.open = slider.value / MAX_ANGLE;
        speed.open = 0;
        slider.style.setProperty('--p', (slider.value / MAX_ANGLE * 100) + '%');
        if (readout) readout.textContent = slider.value + '°';
        request();
      });
      slider.addEventListener('change', function () { sliding = false; });
    }
    presetBtns.forEach(function (btn) {
      btn.addEventListener('click', function () { go(btn.getAttribute('data-preset')); });
    });
    partBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        show(btn.getAttribute('data-part'));
        // On a phone the model can be off screen when a card is tapped: bring it back into view.
        var r = stage.getBoundingClientRect();
        if (r.bottom < 80 || r.top > window.innerHeight - 80) stage.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      });
    });
    if (playBtn) {
      playBtn.addEventListener('click', function () { if (film >= 0) takeover(); else startFilm(); });
      setPlayLabel();
    }

    function fit() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      var box = canvas.parentNode, w = box.clientWidth, h = box.clientHeight;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      aspect = w / Math.max(h, 1);
      var half = 15 * Math.PI / 180;
      var halfH = Math.atan(Math.tan(half) * aspect);
      distance = Math.max(72 / Math.sin(half), 96 / Math.sin(halfH));
      request();
    }

    function load() {
      if (loading) return;
      loading = true;
      Promise.all([modelUrl, glassesUrl].map(function (u) {
        return fetch(u).then(function (r) { if (!r.ok) throw new Error('model'); return r.json(); });
      })).then(function (all) {
        var data = all[0], gdata = all[1];
        data.parts.forEach(function (p) {
          var flat = flatten(b64(p.pos, Int16Array), b64(p.idx, Uint16Array), data.unit);
          if (p.key === 'housing') {
            var keep = [], ink = [];
            for (var t = 0; t < flat.length; t += 18) {
              var zs = [flat[t + 2], flat[t + 8], flat[t + 14]], xs = [flat[t], flat[t + 6], flat[t + 12]], ys = [flat[t + 1], flat[t + 7], flat[t + 13]];
              var zlo = Math.min(zs[0], zs[1], zs[2]), zhi = Math.max(zs[0], zs[1], zs[2]);
              var ylo = Math.min(ys[0], ys[1], ys[2]), yhi = Math.max(ys[0], ys[1], ys[2]);
              var inBand = Math.min(xs[0], xs[1], xs[2]) > -20 && Math.max(xs[0], xs[1], xs[2]) < 26 && ((ylo > 16 && yhi < 23) || (ylo > -23 && yhi < -16));
              var isInk = inBand && zlo > 3.5 && zhi < 3.85 && (zhi - zlo > 0.2 || zhi < 3.6);
              if (!isInk) { keep.push.apply(keep, flat.subarray(t, t + 18)); continue; }
              if (FLUSH_LETTERING) {
                // The CAD still carries the old VISORDOCK lettering: drop its walls and lift its floor to the face, so the face reads plain.
                if (zhi - zlo > 0.3) continue;
                var tri = flat.slice(t, t + 18); tri[2] = tri[8] = tri[14] = 3.8;
                keep.push.apply(keep, tri);
              } else {
                ink.push.apply(ink, flat.subarray(t, t + 18));
              }
            }
            if (ink.length || FLUSH_LETTERING) {
              var lettering = new Float32Array(ink);
              if (ink.length) parts.push({ key: 'lettering', color: LETTER_COLOR, buf: upload(lettering), count: lettering.length / 6, metal: -1 });
              flat = new Float32Array(keep);
            }
          }
          parts.push({ key: p.key, color: PART_COLORS[p.key] || hexToRgb(p.color), buf: upload(flat), count: flat.length / 6 });
        });
        var card = roundedCardData(CARD_SIZE[0], CARD_SIZE[1], CARD_SIZE[2], 3.18);
        // padded edges: a 14 mm core plus two 3 mm layers stepped in 2.5 mm, so the rim reads soft rather than cut
        var vx = -26 + VISOR[0] / 2, vy = -42 + VISOR[1] / 2, vz = -7.2 - VISOR[2] / 2;
        var layers = [moved(roundedCardData(VISOR[0], VISOR[1], VISOR[2] - 6, 24), vx, vy, vz),
          moved(roundedCardData(VISOR[0] - 5, VISOR[1] - 5, 3, 21.5), vx, vy, vz + VISOR[2] / 2 - 1.5),
          moved(roundedCardData(VISOR[0] - 5, VISOR[1] - 5, 3, 21.5), vx, vy, vz - VISOR[2] / 2 + 1.5)];
        var visor = new Float32Array(layers.reduce(function (s, l) { return s + l.length; }, 0)), vo = 0;
        layers.forEach(function (l) { visor.set(l, vo); vo += l.length; });
        var flap = moved(roundedCardData(112, 64, 2.2, 7), 175, 40, -6.1);
        visorBuffer = upload(visor); visorVerts = visor.length / 6;
        flapBuffer = upload(flap); flapVerts = flap.length / 6;
        fabricTex = fabricTexture(gl);
        var roof = roofData(); roofBuffer = upload(roof); roofVerts = roof.length / 6;
        cardBuffer = upload(card);
        cardVerts = card.length / 6;
        if (el.getAttribute('data-cards')) loadCardAtlas(gl, el.getAttribute('data-cards'), function (tex) { cardAtlas = tex; request(); });
        var notes = foldedNoteData();
        noteTex = noteTexture(gl);
        notesBuffer = upload(notes);
        notesVerts = notes.length / 6;
        var gp = {};
        gdata.parts.forEach(function (p) { gp[p.key] = flatten(b64(p.pos, Int16Array), b64(p.idx, Uint16Array), gdata.unit); });
        glasses = { frame: upload(gp.frame), frameCount: gp.frame.length / 6 };
        var s = 240;
        floorBuffer = upload(new Float32Array([-s, 0, -s, s, 0, s, s, 0, -s, -s, 0, -s, -s, 0, s, s, 0, s]));
        loaded = true;
        el.classList.add('is-loaded');
        if (still) { film = parseFloat(still[1]); inView = false; played = true; }
        fit();
        maybePlay();
      }).catch(function () { el.setAttribute('data-failed', ''); });
    }

    function maybePlay() {
      if (loaded && seen && !played && !reduce) startFilm();
      else request();
    }

    if ('ResizeObserver' in window) new ResizeObserver(fit).observe(canvas.parentNode);
    else window.addEventListener('resize', fit);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) { if (entries[0].isIntersecting) load(); }, { rootMargin: '600px 0px' }).observe(el);
      new IntersectionObserver(function (entries) {
        var e = entries[0];
        inView = e.isIntersecting;
        if (e.intersectionRatio >= 0.55) seen = true;
        if (inView) maybePlay();
      }, { threshold: [0, 0.55] }).observe(stage);
    } else {
      inView = seen = true;
      load();
    }
  }

  function initAll(scope) {
    if (!scope.querySelectorAll) return;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-tour]'), initTour);
  }
  function init() { initAll(document); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
